import { useState, useContext, useEffect, useRef } from "react";
import { CombinedContext } from "../../context/combinedContext";
import { Edit, Camera, User2, Phone } from "lucide-react";
import "./profileStaffCard.css";
import StaffServicesModal from "./staffServicesModal/staffServicesModal";
import StaffInfoModal from "./staffInfoModal/staffInfoModal";
import companyBasedRequest from "../../request/companyBasedRequest";
import translations from "../../translations/translations";
import usersRequest from "../../request/usersRequest";
import generalRequest from "../../request/generalRequest";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileStaffSkeleton from "./profileStaffSkeleton/profileStaffSkeleton";
import ProfileUploadModal from "../teamPage/profilePhotoModal/profilePhotoModal";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

const ProfileStaffCard = () => {
    const { userData, language } = useContext(CombinedContext);

    const [staff, setStaff] = useState(null);
    const [staffServiceIds, setStaffServiceIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeModal, setActiveModal] = useState(null);
    const [shopServices, setShopServices] = useState([]);
    const [shopData, setShopData] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);

    const categoryRefs = useRef({});

    const location = useLocation();

    const navigate = useNavigate();

    useEffect(() => {
        if (!location.state?.openModal) return;
        setActiveModal(location.state.openModal);
    }, [location.state, navigate]);

    useEffect(() => {
        if (!userData?.user) return;

        setStaff({
            userId: userData.user.userId,
            firstName: userData.user.firstName,
            lastName: userData.user.lastName,
            image: userData.user.image || null,
            bio: userData.user.bio || "",
            phone: userData.user.phoneNumber || "",
            hours: userData.user.hours || [],
            photo: userData.user.photo || null,
        });

        setStaffServiceIds(userData.user.services || []);
        setLoading(false);
    }, [userData]);

    /* -----------------------------
	   Load shop services
	------------------------------ */
    useEffect(() => {
        if (!userData?.user?.shopId) return;

        const load = async () => {
            try {
                const res = await companyBasedRequest.getShopServices(
                    userData.user.shopId,
                    userData.cognitoTokens.AccessToken
                );
                setShopServices(res.services || []);
            } catch (err) {
                console.error("Failed to fetch shop services:", err);
            }
        };

        load();
    }, [userData]);

    useEffect(() => {
        if (!userData?.user?.shopId) return;

        const load = async () => {
            try {
                const res = await companyBasedRequest.getShopWithstaffsByIdPrivate(
                    userData.user.shopId,
                    userData.cognitoTokens.AccessToken
                );

                // IMPORTANT FIX: real shop data is inside res.shop
                setShopData(res.shop);
            } catch (err) {
                console.error("Failed to fetch shop data:", err);
            }
        };

        load();
    }, [userData]);

    const displayServices = staffServiceIds.map((id) => shopServices.find((s) => s.serviceId === id)).filter(Boolean);

    useEffect(() => {
        if (!shopData?.serviceCategories || !displayServices.length) return;

        const firstCat = shopData.serviceCategories.find((cat) =>
            displayServices.some((s) => s.categoryId === cat.categoryId)
        );

        if (firstCat) {
            setActiveCategory(firstCat.categoryId);
        }
    }, [shopData, displayServices]);

    const uncategorizedServices = displayServices.filter(
        (s) => !s.categoryId || !shopData?.serviceCategories?.some((cat) => cat.categoryId === s.categoryId)
    );

    const staffServicesByCategory = [
        // Normal categories
        ...(shopData?.serviceCategories || [])
            .map((cat) => ({
                category: cat,
                services: displayServices.filter((s) => s.categoryId === cat.categoryId),
            }))
            .filter((group) => group.services.length > 0),

        // Fallback category
        ...(uncategorizedServices.length
            ? [
                  {
                      category: {
                          categoryId: "uncategorized",
                          name: {
                              en: "Other",
                              es: "Otros",
                          },
                      },
                      services: uncategorizedServices,
                  },
              ]
            : []),
    ];

    function formatPhone(number) {
        if (!number) return "";
        // remove all non-digits
        const digits = number.replace(/\D/g, "");

        // US/Canada format (E.164: +1xxxxxxxxxx)
        if (digits.length === 11 && digits.startsWith("1")) {
            return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
        }

        // fallback (just add + if missing)
        return number.startsWith("+") ? number : "+" + number;
    }

    const formatUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return `https://${url}`;
    };

	function formatTime24To12(time24, language = "en") {
        if (!time24) return "";

        const [h, m] = time24.split(":").map(Number);
        const hour12 = h % 12 || 12;
        const minutes = m.toString().padStart(2, "0");
        const period = h >= 12 ? "PM" : "AM";

        return `${hour12}:${minutes} ${period}`;
    }

    const addressQuery = shopData
        ? encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`)
        : "";

    if (loading || !staff || !shopData) return <ProfileStaffSkeleton />;

    return (
        <div className="sf-staffpage">
            {/* HEADER */}
            <div className="sf-staffpage-div">
                <div className="sf-header-clean">
                    <div className="sf-header-left">
                        <div className="sf-avatar-wrapper">
                            {staff.photo ? (
                                <img src={staff.photo} alt="" className="sf-avatar" />
                            ) : (
                                <div className="sf-avatar-fallback">{staff.firstName.charAt(0)}</div>
                            )}
                            <button className="sf-avatar-edit-btn" onClick={() => setActiveModal("avatar")}>
                                <Camera size={16} />
                            </button>
                        </div>

                        <div className="sf-header-text">
                            <h1 className="sf-staff-name">
                                {staff.firstName} {staff.lastName}
                            </h1>
                            {/* BIO SECTION */}
                            {staff.bio && <p className="sf-staff-bio">{staff.bio}</p>}
                        </div>
                        <button className="sf-header-edit" onClick={() => setActiveModal("info")}>
                            <Edit size={14} />
                        </button>
                    </div>
                </div>

                {/* SERVICES */}
                <div className="sf-services-section">
                    <div className="sf-category-row">
                        <h2 className="sf-section-title">
                            Services I Offer
                            <button className="sf-title-edit" onClick={() => setActiveModal("services")}>
                                <Edit size={14} />
                            </button>
                        </h2>

                        <span className="sf-category-count">{displayServices.length} services</span>
                    </div>

                    {displayServices.length > 0 ? (
                        <div className="sf-staffpage-service-list">
                            {/* 🔝 CATEGORY TABS */}
                            <div className="sf-profile-tabs sf-profile-tabs--sticky">
                                {staffServicesByCategory.map(({ category }) => (
                                    <button
                                        key={category.categoryId}
                                        className={`sf-profile-tab-btn ${
                                            activeCategory === category.categoryId ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setActiveCategory(category.categoryId);
                                            categoryRefs.current[category.categoryId]?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }}
                                    >
                                        {category.name?.[language]}
                                    </button>
                                ))}
                            </div>

                            {/* 📦 SERVICES BY CATEGORY */}
                            {staffServicesByCategory.map(({ category, services }) => (
                                <div
                                    key={category.categoryId}
                                    ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                                    className="sf-staffpage-category-section"
                                >
                                    <h3 className="sf-staffpage-category-title">{category.name?.[language]}</h3>

                                    {services.map((s) => (
                                        <div key={s.serviceId}>
                                            <div className="sf-staffpage-service-item">
                                                <div className="sf-staffpage-service-info">
                                                    <div className="sf-staffpage-service-name">
                                                        {s.name}
                                                        {s.isHomeService && (
                                                            <span className="sf-staffpage-service-home">
                                                                <span className="sf-staffpage-dot"> · </span>
                                                                {language === "es" ? "Domicilio" : "Home service"}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="sf-staffpage-service-duration">{s.description}</div>
                                                </div>

                                                <div className="sf-staffpage-service-right">
                                                    <div className="sf-staffpage-service-price">
                                                        {s.currency || "USD"} {s.price ?? 0}
                                                    </div>
                                                    <div className="sf-staffpage-service-duration">
                                                        {s.duration} min
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="sf-service-divider-shop" />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="sf-empty" onClick={() => setActiveModal("services")}>
                            + Add services
                        </div>
                    )}
                </div>
            </div>

            {/* SIDEBAR */}
            <div className="sf-staffpage-sidebar">
                {/* MAP */}
                <div className="sf-staffpage-sidebar-section">
                    <h3 className="sf-staffpage-sidebar-title">
                        {translations[language].profile.sidebar.location.title}
                    </h3>

                    <ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
                </div>

                {/* STAFF LIST (full sidebar like admin) */}
                <div className="sf-shop-sidebar-section">
                    <div className="sf-shop-sidebar-title-row">
                        <h3 className="sf-shop-sidebar-title">{translations[language].shopPage.sidebar.staff}</h3>
                    </div>

                    <div className="sf-staffpage-staff-list">
                        {shopData.staffs?.map((b) => (
                            <div className="sf-staffpage-staff-item" key={b.staffId}>
                                {b.image ? (
                                    <img className="sf-staffpage-staff-avatar" src={b.image} alt={b.firstName} />
                                ) : (
                                    <div className="sf-staffpage-staff-avatar-fallback">
                                        <span>{b.firstName?.charAt(0).toUpperCase()}</span>
                                    </div>
                                )}
                                <div className="sf-staffpage-staff-name">
                                    {b.firstName} {b.lastName}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CONTACT */}
                <div className="sf-staffpage-sidebar-section">
                    <h3 className="sf-staffpage-sidebar-title">
                        {translations[language].profile.sidebar.contact.title}
                    </h3>

                    <p className="sf-staffpage-contact">
                        <Phone size={16} className="sf-staffpage-contact-icon" />
                        <span>
                            {shopData.phone
                                ? formatPhone(shopData.phone)
                                : translations[language].profile.sidebar.contact.noPhone}
                        </span>
                    </p>

                    <hr className="sf-shop-divider" />

                    {/* HOURS */}
                    {shopData.openingHours?.length > 0 ? (
                        <div>
                            <h3 className="sf-staffpage-sidebar-title">
                                {translations[language].profile.sidebar.hours.title}
                            </h3>

                            <div className="sf-shop-hours-full">
                                {shopData.openingHours.map((day, index) => {
                                    const isToday = new Date().getDay() === index;
                                    const dayName = language === "es" ? day.dayES : day.dayEN;

                                    return (
                                        <div key={index} className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}>
                                            <div className="sf-shop-hours-day">{dayName}</div>
                                            <div className="sf-shop-hours-time">
    {day.closed
        ? translations[language].profile.sidebar.hours.closed
        : `${formatTime24To12(day.open, language)} - ${formatTime24To12(day.close, language)}`}
</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <span className="sf-placeholder">Add opening hours</span>
                    )}
                </div>

                {/* SOCIAL LINKS */}
                {shopData.socialLinks && Object.values(shopData.socialLinks).some((v) => v) && (
                    <div className="sf-staffpage-sidebar-section">
                        <hr className="sf-shop-divider" />

                        <h3 className="sf-staffpage-sidebar-title">
                            {translations[language].shopPage.sidebar.socialTitle}
                        </h3>

                        <div className="sf-shop-socials">
                            {shopData.socialLinks.instagram && (
                                <div className="sf-social-item">
                                    <a
                                        href={formatUrl(shopData.socialLinks.instagram)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sf-social-link"
                                    >
                                        <img
                                            src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png"
                                            alt="Instagram"
                                        />
                                    </a>
                                    <p>Instagram</p>
                                </div>
                            )}

                            {shopData.socialLinks.facebook && (
                                <div className="sf-social-item">
                                    <a
                                        href={formatUrl(shopData.socialLinks.facebook)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sf-social-link"
                                    >
                                        <img
                                            src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png"
                                            alt="Facebook"
                                        />
                                    </a>
                                    <p>Facebook</p>
                                </div>
                            )}

                            {shopData.socialLinks.tiktok && (
                                <div className="sf-social-item">
                                    <a
                                        href={formatUrl(shopData.socialLinks.tiktok)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sf-social-link"
                                    >
                                        <img
                                            src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png"
                                            alt="TikTok"
                                        />
                                    </a>
                                    <p>TikTok</p>
                                </div>
                            )}

                            {shopData.socialLinks.website && (
                                <div className="sf-social-item">
                                    <a
                                        href={formatUrl(shopData.socialLinks.website)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sf-social-link"
                                    >
                                        <img
                                            src="https://cdn-icons-png.flaticon.com/512/545/545674.png"
                                            alt="Website"
                                        />
                                    </a>
                                    <p>{translations[language].shopPage.sidebar.websiteTitle}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* MODALS */}
            {activeModal === "info" && (
                <StaffInfoModal open onClose={() => setActiveModal(null)} staff={staff} setStaff={setStaff} />
            )}

            {activeModal === "services" && (
                <StaffServicesModal
                    isOpen
                    onClose={() => setActiveModal(null)}
                    shopServices={shopServices}
                    staffServices={staffServiceIds}
                    onSave={(updatedIds) => setStaffServiceIds(updatedIds)}
                />
            )}
            {activeModal === "avatar" && (
                <ProfileUploadModal
                    isOpen
                    staffProfile={{
                        userId: staff.userId,
                        photo: staff.image,
                    }}
                    onClose={() => setActiveModal(null)}
                    onUploaded={(newUrl) => {
                        setStaff((prev) => ({
                            ...prev,
                            photo: newUrl,
                        }));

                        setShopData((prev) => ({
                            ...prev,
                            staffs: prev.staffs.map((s) => (s.userId === staff.userId ? { ...s, photo: newUrl } : s)),
                        }));
                    }}
                />
            )}
        </div>
    );
};

export default ProfileStaffCard;
